import type { VersionedVisit } from "@/features/ryteTracker/store/ryteDataSlice";
import type { Goal } from "@/features/ryteTracker/types/goal";
import { normalizeIdentityToken } from "@/features/ryteTracker/utils/identity/resolveDemandId";
import { identityFromVisitReason } from "@/features/ryteTracker/utils/identity/visitReasonIdentity";
import { resolveVisitEntityId } from "@/features/ryteTracker/utils/pillEnrichment/resolveVisitEntityId";
import {
  collectDemandValues,
  declaresOperationalId,
  resolveAllDemandIds,
  resolveDemandId,
  type ResolvedDemandId,
} from "@/features/ryteTracker/utils/identity/resolveDemandId";

/**
 * Written by the slice on every accepted Visit upsert and RETAINED across visit
 * removal — equipment leaving the yard does not make an identity link untrue, and
 * that retention is what lets a VRID-only goal keep resolving its visitId.
 */
export interface DemandAliases {
  vridToVisitId: Record<string, string>;
  isaToVisitId: Record<string, string>;
  /**
   * Plan → VRID from visit haul identifiers: the only source that labels a
   * plan-only goal with no visit linkage, no sibling VRID and no appointment.
   * Bounded staleness accepted — a newer visit reporting the same plan id
   * overwrites, so only a plan detached everywhere keeps a stale entry.
   *
   * `visitId` rides along so mock teardown can evict by originating visit, which a
   * bare `planId → vrid` map cannot do.
   */
  locationPlanToVrid: Record<string, { vrid: string; visitId: string }>;
}

/** Reverse indexes built once per visit array; `byVrid` / `byIsa` read `activeVisitReasons`. */
export interface VisitDemandLookup {
  byVisitId: Record<string, VersionedVisit>;
  byVrid: Record<string, VersionedVisit>;
  byIsa: Record<string, VersionedVisit>;
  /**
   * Both maps below are OPTIONAL so hand-built fixtures and the projector's own
   * lookups degrade to the pre-feature answer (no alias fallback / no plan
   * two-hop) instead of failing to compile.
   */
  demandAliases?: DemandAliases;
  /** Appointment-sourced — see `buildLocationPlanToVridMap`. */
  locationPlanToVrid?: Record<string, string>;
}

/** What ONE visit reports, as key lists rather than as an index — the shape the slice's alias refresh needs. */
export interface VisitDemandIdentity {
  /** `""` when neither id path resolves — callers skip such a visit. */
  visitId: string;
  /**
   * Raw-then-canonical spelling per reason. BOTH are kept: `byVrid` is probed with
   * the goal's un-normalized `demandId.VRID` and with the normalized
   * appointment-sourced value from the plan two-hop.
   */
  vrids: string[];
  isaIds: string[];
  /**
   * Plan → VRID from this visit's haul identifiers, active reasons before
   * `originalVisitReasons` — the fallback matters because a trailer whose haul has
   * executed reports `activeVisitReasons: []` while still carrying the pairing an
   * LP-primary goal's whole label depends on.
   *
   * **DISPLAY identity only.** This must never reach `locationPlanToVrid`, whose
   * two-hop decides which dock-door ROW a puck occupies: a visit-sourced pairing
   * can name a TRACTOR's visit (a tractor dispatched to execute the plan carries
   * the plan's VRID), whose events describe movement to a parking slip — resolving
   * a LOAD goal onto it re-anchors the puck to a row that may not exist, so it
   * disappears from the chart rather than merely mislabelling. Same reason `vrids`
   * / `isaIds` stay ACTIVE-only while this accepts historical reasons.
   *
   * `visitId` rides along so mock teardown can evict by originating visit.
   */
  planPairs: Record<string, { vrid: string; visitId: string }>;
}

/**
 * First-write-wins per plan id. Cross-upsert conflicts are the slice's to observe —
 * it sees pairings across upserts and owns that warn.
 */
export const extractVisitDemandIdentity = (visit: VersionedVisit): VisitDemandIdentity => {
  const visitId = resolveVisitEntityId(visit);
  const vrids: string[] = [];
  const isaIds: string[] = [];
  const planPairs = Object.create(null) as Record<string, { vrid: string; visitId: string }>;

  const recordPlanPair = (planId: string, rawVrid: string): void => {
    const vrid = normalizeIdentityToken(rawVrid);
    if (!planId || !vrid || !visitId || planPairs[planId]) return;
    planPairs[planId] = { vrid, visitId };
  };

  const details = visit.data?.equipment?.visitDetails;

  for (const reason of details?.activeVisitReasons ?? []) {
    const { vrId, isaId, locationPlanId } = identityFromVisitReason(reason);
    if (vrId) {
      vrids.push(vrId);
      // The canonical spelling goes in ALONGSIDE the raw one, not instead of it
      // — see `vrids`.
      const canonical = normalizeIdentityToken(vrId);
      if (canonical && canonical !== vrId) vrids.push(canonical);
    }
    if (isaId) isaIds.push(isaId);
    // `locationPlanId` sits beside `vrId` in the SAME `vrHaulIdentifiers` struct
    // this loop already reads, so the pairing costs no extra traversal.
    recordPlanPair(locationPlanId, vrId);
  }

  // Fallback pass, plan→VRID pairing ONLY — never `vrids` / `isaIds`. See
  // `planPairs`.
  for (const reason of details?.originalVisitReasons ?? []) {
    const { vrId, locationPlanId } = identityFromVisitReason(reason);
    recordPlanPair(locationPlanId, vrId);
  }

  return { visitId, vrids, isaIds, planPairs };
};

/**
 * First-match-wins per key across the visit array.
 *
 * `Object.create(null)`: every key comes from the API / WebSocket / URL, so an
 * attacker-chosen `__proto__` would otherwise pollute `Object.prototype`
 * (`architecture/redux-patterns.md` "Prototype-pollution hardening").
 */
export const buildVisitDemandLookup = (
  visits: VersionedVisit[],
  demandAliases?: DemandAliases,
  locationPlanToVrid?: Record<string, string>
): VisitDemandLookup => {
  const byVisitId = Object.create(null) as Record<string, VersionedVisit>;
  const byVrid = Object.create(null) as Record<string, VersionedVisit>;
  const byIsa = Object.create(null) as Record<string, VersionedVisit>;

  for (const visit of visits) {
    // `planPairs` is deliberately NOT read here: a visit-sourced pairing is
    // display identity, and these indexes decide which visit — and therefore
    // which dock-door row — a goal resolves onto. See `VisitDemandIdentity`.
    const { visitId, vrids, isaIds } = extractVisitDemandIdentity(visit);
    if (visitId) {
      byVisitId[visitId] = visit;
    }
    for (const vrid of vrids) {
      if (!byVrid[vrid]) byVrid[vrid] = visit;
    }
    for (const isaId of isaIds) {
      if (!byIsa[isaId]) byIsa[isaId] = visit;
    }
  }

  return {
    byVisitId,
    byVrid,
    byIsa,
    demandAliases,
    locationPlanToVrid,
  };
};

/**
 * Only the two plan→VRID sources are required, not a whole `VisitDemandLookup`
 * — a full lookup still satisfies this structurally. Keeps light consumers
 * (action modals) off `enrichedPuckSelectors`, whose import graph drags in the
 * whole projector.
 */
export type LocationPlanVridSource = Pick<
  VisitDemandLookup,
  "locationPlanToVrid" | "demandAliases"
>;

/**
 * VRID derived for a locationPlanId-only goal — undefined for every other demand
 * type, since a goal declaring a VRID has no DERIVED one.
 *
 * **Appointment before visit**, and this file owns that precedence so the pill,
 * the modals and `selectDerivedVridsByGoalId` cannot rank them differently. The
 * visit-sourced pairing is bounded-stale BY DESIGN (retained across visit
 * removal), so a disagreement is known staleness rather than a real conflict —
 * the loser is dropped, not surfaced as a `+N`. Reads the persisted ALIAS map
 * because only that one survives the originating visit leaving the yard, which is
 * the continuity this source exists to provide.
 *
 * **Never copy the result onto `goal.demandId`.** It is display + join metadata;
 * on a goal with no plan id of its own it would become the server-side primary at
 * the next write.
 */
export const resolveDerivedVrid = (
  resolved: ResolvedDemandId,
  lookup: LocationPlanVridSource
): string | undefined => {
  if (resolved.type !== "locationPlanId") return undefined;
  return (
    lookup.locationPlanToVrid?.[resolved.value] ??
    lookup.demandAliases?.locationPlanToVrid?.[resolved.value]?.vrid
  );
};

/** Prefer this at call sites; the two-step form only when the resolved id is in hand. */
export const derivedVridForDemandId = (
  demandId: Goal["demandId"],
  lookup: LocationPlanVridSource
): string | undefined => resolveDerivedVrid(resolveDemandId(demandId), lookup);

/**
 * Use for every "is this the same work?" comparison, and pass the SAME
 * `planVridSource` to both sides so the test is symmetric. Two silent misses it
 * closes, both of which produced DUPLICATE OPERATIONAL WORK rather than a
 * cosmetic glitch:
 *
 *  1. a created goal is routinely `{locationPlanId, visitId}` (writes rank LP
 *     first) while the appointment for the same haul is keyed by VRID, so
 *     declared-only sets do not intersect;
 *  2. every derived VRID is normalized while goals and cards keep GAM's raw
 *     spelling, so one VRID can differ by case alone across the two sides.
 */
export const collectMatchableDemandValues = (
  demandId: Goal["demandId"],
  planVridSource?: LocationPlanVridSource
): string[] => {
  const values = new Set<string>();
  const add = (value: string | undefined): void => {
    if (!value) return;
    values.add(value);
    const normalized = normalizeIdentityToken(value);
    if (normalized) values.add(normalized);
  };
  for (const value of collectDemandValues(demandId)) add(value);
  if (planVridSource) add(derivedVridForDemandId(demandId, planVridSource));
  return Array.from(values);
};

/**
 * A derived VRID supersedes any primary the goal did NOT declare — plan id and
 * visitId alike — so a label-by-TYPE consumer renders the VRID label rather than a
 * Location-Plan / Visit label beside a VRID value.
 *
 * Gating on `declaresOperationalId` rather than on `locationPlanId` is what keeps
 * the surfaces agreeing: a visitId-only goal derives a VRID from the event trail
 * too, and the narrower gate made the detail panel's Demand ID row read `Visit: …`
 * under a heading and pill that both read `VR-…` for the same goal.
 */
export const resolvePresentedDemandId = (
  demandId: Goal["demandId"],
  derivedVrid: string | undefined
): ResolvedDemandId =>
  derivedVrid && !declaresOperationalId(demandId)
    ? { type: "VRID", value: derivedVrid }
    : resolveDemandId(demandId);

/**
 * A plan id resolves via the two-hop plan → VRID → `byVrid`, and only through the
 * APPOINTMENT-sourced map: this answer decides which dock-door row a puck occupies,
 * and a visit-sourced pairing can name a tractor's visit (see
 * `VisitDemandIdentity.planPairs`). Undefined when no appointment carries the plan id.
 */
export const resolveVisitFromDemand = (
  resolved: ResolvedDemandId,
  lookup: VisitDemandLookup
): VersionedVisit | undefined => {
  switch (resolved.type) {
    case "visitId":
      return lookup.byVisitId[resolved.value];
    case "VRID":
      return lookup.byVrid[resolved.value];
    case "ISA":
      return lookup.byIsa[resolved.value];
    case "locationPlanId": {
      const vrid = lookup.locationPlanToVrid?.[resolved.value];
      return vrid ? lookup.byVrid[vrid] : undefined;
    }
  }
};

/**
 * "Which visit's events belong to this goal's trailer?" — the operational
 * visit-anchor for cross-entity joins, where `resolveVisitId` answers the
 * narrower direct-only question (DOM attributes, wire payloads).
 *
 * Five rungs, each covering a shape the one above misses: the engine's
 * authoritative `goal.visitId`; the lookup; the directly-pinned
 * `demandId.visitId` (for visits whose `activeVisitReasons` do not carry the VRID
 * linkage yet); `resolved.value` for a visit-only demand the lookup has not
 * indexed; and finally `demandAliases`, which is the event-continuity mechanism —
 * the live Visit is gone but a VRID-only puck keeps the visitId its events are
 * keyed by. Must stay in step with `makeSelectEnrichedPuck`.
 */
export const resolveVisitIdViaLookup = (
  goal: Pick<Goal, "demandId" | "visitId">,
  lookup: VisitDemandLookup
): string | undefined => {
  const goalVisitId = goal.visitId?.trim();
  if (goalVisitId) return goalVisitId;
  const resolved = resolveDemandId(goal.demandId);
  const visit = resolveVisitFromDemand(resolved, lookup);
  if (visit) {
    const fromVisit = resolveVisitEntityId(visit) || undefined;
    if (fromVisit) return fromVisit;
  }
  const direct = resolveAllDemandIds(goal.demandId).visitId;
  if (direct) return direct;
  if (resolved.type === "visitId" && resolved.value.length > 0) return resolved.value;
  // Alias fallback: the Visit entity is gone but the lightweight
  // VRID→visitId / ISA→visitId alias persists from the last upsert.
  const aliases = lookup.demandAliases;
  if (aliases) {
    if (resolved.type === "VRID") {
      const aliased = aliases.vridToVisitId[resolved.value];
      if (aliased) return aliased;
    }
    if (resolved.type === "ISA") {
      const aliased = aliases.isaToVisitId[resolved.value];
      if (aliased) return aliased;
    }
  }
  return undefined;
};
